import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { milestoneService, projectService } from "../services/edosService";

export default function MilestoneCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const defaultProjectId = searchParams.get("projectId") || "";

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    projectId: defaultProjectId,
    milestoneName: "",
    dueDate: "",
    description: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    if (!form.projectId || !form.milestoneName || !form.dueDate) {
      toast({
        title: "Project, milestone name and due date are required",
        status: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await milestoneService.create(form);
      const created = res?.data?.data || res?.data?.milestone || res?.data;
      toast({ title: "Milestone created", status: "success" });

      if (created?._id) {
        navigate(`/edos/milestones/${created._id}`);
      } else {
        navigate(`/edos/milestones?projectId=${form.projectId}`);
      }
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to create milestone",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={6}>
      <HStack mb={5} spacing={3}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={() =>
            navigate(
              `/edos/milestones${form.projectId ? `?projectId=${form.projectId}` : ""}`,
            )
          }
        >
          Back
        </Button>
        <Text fontSize="2xl" fontWeight="bold">
          Create Milestone
        </Text>
      </HStack>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        maxW="760px"
      >
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>Project</FormLabel>
            <Select
              value={form.projectId}
              onChange={(e) =>
                setForm((f) => ({ ...f, projectId: e.target.value }))
              }
            >
              <option value="">Select project...</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Milestone Name</FormLabel>
            <Input
              value={form.milestoneName}
              onChange={(e) =>
                setForm((f) => ({ ...f, milestoneName: e.target.value }))
              }
              placeholder="e.g. UAT Sign-off"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Due Date</FormLabel>
            <Input
              type="date"
              value={form.dueDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, dueDate: e.target.value }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Input
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="Optional description"
            />
          </FormControl>

          <HStack justify="flex-end" pt={2}>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(
                  `/edos/milestones${form.projectId ? `?projectId=${form.projectId}` : ""}`,
                )
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              isLoading={submitting}
              onClick={handleCreate}
            >
              Create Milestone
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}
